/**
 * Karana Shashtyamsha D60 Calculation Logic
 * Based on Bhrigu Muni teachings - 60 divisions for karmic causes
 *
 * Structure:
 * - 1 fixed KIMSTUGHNA (position 1)
 * - 56 movable Karanas in 7 cycles (positions 2-57): BAVA, BALAVA, KAULAVA, TAITILA, GARIJA, VANIJA, VISHTI
 * - 3 fixed ending Karanas: SHAKUNI (58), CHATUSHPADA (59), NAGA (60)
 *
 * Formula: Karana_number = floor(degrees_in_rashi ÷ 0.5) + 1
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number; // Parsed from position string
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 11 unique Karana types
 */
export const KARANA_TYPES = [
  'KIMSTUGHNA',   // Fixed #1
  'BAVA',         // Movable #1
  'BALAVA',       // Movable #2
  'KAULAVA',      // Movable #3
  'TAITILA',      // Movable #4
  'GARIJA',       // Movable #5
  'VANIJA',       // Movable #6
  'VISHTI',       // Movable #7
  'SHAKUNI',      // Fixed #58
  'CHATUSHPADA',  // Fixed #59
  'NAGA'          // Fixed #60
] as const;

export type KaranaType = typeof KARANA_TYPES[number];

/**
 * 7 Grahas (planetary rulers) for movable Karanas
 */
export const GRAHA_TYPES = ['SURYA', 'CHANDRA', 'MANGALA', 'BUDHA', 'GURU', 'SHUKRA', 'SHANI'] as const;
export type GrahaType = typeof GRAHA_TYPES[number];

/**
 * Karana descriptions with Sanskrit names, ruling Graha, and Devata
 */
export const KARANA_DESCRIPTIONS: Record<KaranaType, {
  sanskrit: string;
  graha: string;
  devata: string;
  meaning: string;
  type: 'fixed' | 'movable';
}> = {
  KIMSTUGHNA: {
    sanskrit: 'किंस्तुघ्न',
    graha: 'Ke',
    devata: 'Maruta',
    meaning: 'Who destroys, why kill? Questioning death and changes',
    type: 'fixed'
  },
  BAVA: {
    sanskrit: 'भव',
    graha: 'Sy',
    devata: 'Indra',
    meaning: 'Fire born from lightning',
    type: 'movable'
  },
  BALAVA: {
    sanskrit: 'बालव',
    graha: 'Ch',
    devata: 'Brahma',
    meaning: 'Strength, power, potential',
    type: 'movable'
  },
  KAULAVA: {
    sanskrit: 'कौलव',
    graha: 'Ma',
    devata: 'Mitra',
    meaning: 'Lineage, family, community',
    type: 'movable'
  },
  TAITILA: {
    sanskrit: 'तैतिल',
    graha: 'Bu',
    devata: 'Aryama',
    meaning: 'Bowl, vessel, measurement',
    type: 'movable'
  },
  GARIJA: {
    sanskrit: 'गरिज',
    graha: 'Gu',
    devata: 'Bhu',
    meaning: 'Importance, respectability, significance, weight',
    type: 'movable'
  },
  VANIJA: {
    sanskrit: 'वणिज',
    graha: 'Sk',
    devata: 'Lakshmi',
    meaning: 'Trade, commerce, exchange',
    type: 'movable'
  },
  VISHTI: {
    sanskrit: 'विष्टि',
    graha: 'Sa',
    devata: 'Yama',
    meaning: 'Hard work, service, compulsion, deprivation',
    type: 'movable'
  },
  SHAKUNI: {
    sanskrit: 'शकुनि',
    graha: 'Ra',
    devata: 'Kali',
    meaning: 'Omens, bird behavior, strong dependency, treachery',
    type: 'fixed'
  },
  CHATUSHPADA: {
    sanskrit: 'चतुष्पद',
    graha: 'Ke',
    devata: 'Vrisha',
    meaning: 'Four legs, supports, four steps',
    type: 'fixed'
  },
  NAGA: {
    sanskrit: 'नाग',
    graha: 'Ra',
    devata: 'Sarpa',
    meaning: 'Serpent, underground, winding movement, mystical power',
    type: 'fixed'
  }
};

/**
 * Calculate Karana number from degrees in rashi (0-30)
 */
export function calculateKaranaNumber(degreeInRashi: number): number {
  // Each Karana = 0.5° (30 arc minutes)
  // Karana_number = floor(degrees ÷ 0.5) + 1
  const karanaIndex = Math.floor(degreeInRashi / 0.5);
  return Math.min(karanaIndex + 1, 60);
}

/**
 * Get Karana type from Karana number (1-60)
 */
export function getKaranaType(karanaNumber: number): KaranaType {
  if (karanaNumber === 1) {
    return 'KIMSTUGHNA';
  } else if (karanaNumber >= 2 && karanaNumber <= 57) {
    // Movable Karanas (7 types repeating)
    const positionInCycle = ((karanaNumber - 2) % 7) + 1;
    const movableKaranas: KaranaType[] = ['BAVA', 'BALAVA', 'KAULAVA', 'TAITILA', 'GARIJA', 'VANIJA', 'VISHTI'];
    return movableKaranas[positionInCycle - 1];
  } else if (karanaNumber === 58) {
    return 'SHAKUNI';
  } else if (karanaNumber === 59) {
    return 'CHATUSHPADA';
  } else {
    return 'NAGA';
  }
}

/**
 * Get cycle number for movable Karanas (1-7)
 * Returns null for fixed Karanas
 */
export function getCycleNumber(karanaNumber: number): number | null {
  if (karanaNumber >= 2 && karanaNumber <= 57) {
    return Math.floor((karanaNumber - 2) / 7) + 1;
  }
  return null;
}

/**
 * Single planet's Karana Shashtyamsha attributes
 */
export interface KaranaShashtyamshaAttributes {
  planet: PlanetDisplay;
  karanaNumber: number;
  karanaType: KaranaType;
  cycleNumber: number | null;
  interpretation: string;
}

/**
 * Complete Karana Shashtyamsha data for all planets
 */
export interface KaranaShashtyamshaData {
  divisions: KaranaShashtyamshaAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Karana Shashtyamsha for all planets
 */
export function calculateKaranaShashtyamshas(apiResponse: JatakaResponse): KaranaShashtyamshaData {
  const divisions: KaranaShashtyamshaAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    // Convert to PlanetDisplay format
    const planetDisplay = convertToPlanetDisplay(planet, currentHoraPlanet);

    // Calculate degree within current rashi (0-30)
    const degreeInRashi = planetDisplay.longitude % 30;

    // Calculate Karana number (1-60)
    const karanaNumber = calculateKaranaNumber(degreeInRashi);

    // Get Karana type
    const karanaType = getKaranaType(karanaNumber);

    // Get cycle number (for movable Karanas)
    const cycleNumber = getCycleNumber(karanaNumber);

    // Get Karana info
    const karanaInfo = KARANA_DESCRIPTIONS[karanaType];

    // Create interpretation
    const interpretation = cycleNumber
      ? `${karanaInfo.meaning} (Cycle ${cycleNumber})`
      : karanaInfo.meaning;

    divisions.push({
      planet: planetDisplay,
      karanaNumber,
      karanaType,
      cycleNumber,
      interpretation
    });
  });

  return { divisions };
}
